/**
 * # Eurail Page
 *
 */
import { Typography } from '@yiluhub/ui-elements';
import { ElementsSDK, UiUtilities } from '@yiluhub/ui-sdk-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { PassType } from '../../utils/types';
import { EurailSipSearchForm } from './components/EurailSipSearchForm';
import useEurailSearchInputPage from './hooks/';
import './styles.scss';

type SearchInputPageProps = {
  initialPassType?: PassType;
};

const SearchInputPage: React.FC<SearchInputPageProps> = ({ initialPassType }) => {
  const { t } = useTranslation();
  const { isDesktop } = UiUtilities.useIsDesktop();
  const eurailSearchFormProps = useEurailSearchInputPage(initialPassType);
  const isGlobalPass = eurailSearchFormProps.searchInput.pass === PassType.GLOBAL;

  const imageSrc = (
    <div className="eurail-image-container">
      <img
        src={`https://provider-resources-production-v2.s3.eu-central-1.amazonaws.com/eurail/image/${
          isGlobalPass ? 'global' : 'local'
        }-pass.png`}
        alt="Colorful coastal town"
      />
    </div>
  );

  return (
    <div className="eurail-pass-page">
      <section className="eurail-banner-section">
        <ElementsSDK.Message
          type={ElementsSDK.MessageType.INFORMATIVE}
          content={t('eurail.banner.alert')}
          className="eurail-banner-message"
        />
        {!isDesktop && imageSrc}
        <div className="eurail-form-container">
          <div
            className="eurail-sip-form"
            aria-label="Purchase eurail passes for your next travel"
            aria-level={1}
          >
            <Typography variant="h2" className="eurail-sip-title">
              {t('eurail.sip.title')}
            </Typography>
            <Typography variant="p1" className="eurail-sip-description">
              {t('eurail.sip.description')}
            </Typography>
            <EurailSipSearchForm {...eurailSearchFormProps} />
          </div>
          {isDesktop && imageSrc}
        </div>
      </section>

      <section className="eurail-icons-section">
        <div className="eurail-icon-box">
          <ElementsSDK.Icon.Train />
          <Typography variant="p1">{t('eurail.icon_train')}</Typography>
        </div>
        <div className="eurail-icon-box">
          <ElementsSDK.Icon.Plan />
          <Typography variant="p1">{t('eurail.icon_itinerary')}</Typography>
        </div>
        <div className="eurail-icon-box">
          <ElementsSDK.Icon.Calendar />
          <Typography variant="p1">{t('eurail.icon_flexible')}</Typography>
        </div>
        <div className="eurail-icon-box">
          <ElementsSDK.Icon.Leaf />
          <Typography variant="p1">{t('eurail.icon_sustainable')}</Typography>
        </div>
      </section>

      <section className="eurail-how-it-works">
        <Typography variant="h2">{t('eurail.how_it_works_title')}</Typography>
        <div className="eurail-how-grid">
          <div className="eurail-how-card">
            <ElementsSDK.Icon.Calendar />
            <Typography variant="h3" className="eurail-how-card-title">
              {t('eurail.how_travel_day_title')}
            </Typography>
            <Typography variant="p1">{t('eurail.how_travel_day')}</Typography>
          </div>
          {isGlobalPass && (
            <div className="eurail-how-card">
              <ElementsSDK.Icon.Plan />
              <Typography variant="h3" className="eurail-how-card-title">
                {t('eurail.how_borders_title')}
              </Typography>
              <Typography variant="p1">{t('eurail.how_borders')}</Typography>
            </div>
          )}
          {!isGlobalPass && (
            <div className="eurail-how-card">
              <ElementsSDK.Icon.Passport />
              <Typography variant="h3" className="eurail-how-card-title">
                {t('eurail.how_passport_title')}
              </Typography>
              <Typography variant="p1">{t('eurail.how_passport')}</Typography>
            </div>
          )}
          <div className="eurail-how-card">
            <ElementsSDK.Icon.Seat />
            <Typography variant="h3" className="eurail-how-card-title">
              {t('eurail.how_ready_title')}
            </Typography>
            <Typography variant="p1">{t('eurail.how_ready')}</Typography>
          </div>
        </div>
      </section>

      <section className="eurail-included-section">
        <Typography variant="h2">{t('eurail.included_title')}</Typography>
        <div className="eurail-included-columns">
          <div className="eurail-included-column">
            <div className="eurail-included-item">
              <ElementsSDK.Icon.Check className="check-icon" />
              <Typography variant="p1">{t('eurail.included_train_access')}</Typography>
            </div>
            <div className="eurail-included-item">
              <ElementsSDK.Icon.Check className="check-icon" />
              <Typography variant="p1">{t('eurail.included_delivery')}</Typography>
            </div>
            <div className="eurail-included-item">
              <ElementsSDK.Icon.Check className="check-icon" />
              <Typography variant="p1">{t('eurail.included_flexibility')}</Typography>
            </div>
            <div className="eurail-included-item">
              <ElementsSDK.Icon.Check className="check-icon" />
              <Typography variant="p1">{t('eurail.included_planner_app')}</Typography>
            </div>
          </div>

          <div className="eurail-included-column">
            <div className="eurail-included-item">
              <ElementsSDK.Icon.Cross className="cross-icon" />
              <Typography variant="p1">{t('eurail.not_included_reservations')}</Typography>
            </div>
            <div className="eurail-included-item">
              <ElementsSDK.Icon.Cross className="cross-icon" />
              <Typography variant="p1">{t('eurail.not_included_other_transport')}</Typography>
            </div>
            <div className="eurail-included-item">
              <ElementsSDK.Icon.Cross className="cross-icon" />
              <Typography variant="p1">{t('eurail.not_included_accommodation')}</Typography>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SearchInputPage;
